/**
 * Workspace home (spec §8.3): page tree from the workspace doc (`pages` LoroTree, node data
 * `{docId}`), titles from the registry; create page = client UUIDv7 + registration + tree node.
 */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { UndoManager, type TreeID } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";
import { useEffect, useMemo, useRef, useState } from "react";
import { qk, registerPage, usePages, useWorkspace } from "../api/hooks";
import { addPendingPage } from "../offline/pendingPages";
import type { PageInfo } from "../api/types";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useDoc, useLoroSelector, useSyncStatus } from "../sync/hooks";
import { FolderPlus } from "lucide-react";
import { PageTree } from "./PageTree";
import { readPageRows, samePageRows, type PageRow } from "./pageTree";
import { useWorkspaceRail } from "../rail/WorkspaceRail";
import { RelationsMenu } from "../rail/RelationsMenu";
import type { DocHandle } from "../sync/docManager";
import { LinksPanel } from "../links/LinksPanel";
import { pageNodeOf, pageSubject } from "../links/subject";
import { createFolder, createPageNode } from "./pageTree";
import { addMember, readSequences } from "../sequences/model";
import { useMe } from "../api/hooks";
import { SyncBadge } from "./SyncBadge";
import { SequencesSidebar } from "../sequences/SequencesSidebar";
import { ConceptsPanel } from "../concepts/ConceptsPanel";
import { ImportPageButton } from "../transfer/ImportPageButton";
import { TrashPanel } from "./TrashPanel";
import { useT } from "../i18n";

export function WorkspaceHomePage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const t = useT();
  const ws = useWorkspace(workspaceId);
  const pages = usePages(workspaceId);
  const handle = useDoc("workspace", workspaceId);
  const status = useSyncStatus(handle);
  const sequences = useLoroSelector(handle?.doc ?? null, readSequences, (a, b) => JSON.stringify(a) === JSON.stringify(b)) ?? [];
  const me = useMe();
  const [linkPage, setLinkPage] = useState<string | null>(null);
  const [visibleRows, setVisibleRows] = useState<PageRow[]>([]);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const canWrite = ws.data?.my_role !== "viewer";
  // one undo manager for the home's arrangement (spec §8.25 item 4): a wrong drop is one Ctrl/⌘+Z away
  const undo = useMemo(() => (handle ? new UndoManager(handle.doc, { excludeOriginPrefixes: ["sys:"], mergeInterval: 500 }) : null), [handle]);
  useEffect(() => {
    if (!undo) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undo.undo(); }
      else if ((e.key === "z" && e.shiftKey) || e.key === "y") { e.preventDefault(); undo.redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  const createPage = async (parent?: TreeID, untitled = false) => {
    if (!handle) return;
    const id = uuidv7();
    const pageTitle = (untitled ? "" : title.trim()) || t("page.untitled");
    // add to the workspace doc first (works offline); registration follows when online
    createPageNode(handle.doc, id, parent);
    try {
      await registerPage(workspaceId, id, pageTitle);
      await qc.invalidateQueries({ queryKey: qk.pages(workspaceId) });
    } catch (e) {
      // offline: registered on reconnect (spec §16.2 step 3); the workspace doc already lists it
      console.debug("page registration deferred", e);
      await addPendingPage({ ws: workspaceId, id, title: pageTitle });
      qc.setQueryData(qk.pages(workspaceId), (old: PageInfo[] | undefined) => [
        ...(old ?? []),
        { id, workspace_id: workspaceId, title: pageTitle, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), deleted_at: null, my_mode: "write" as const },
      ]);
    }
    if (!untitled) setTitle("");
    void navigate({ to: "/w/$workspaceId/d/$docId", params: { workspaceId, docId: id } });
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl gap-8 px-9 pb-16 pt-8">
      <div className="grow">
        <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-[26px] font-semibold">{ws.data?.name ?? t("ws.fallbackName")}</h1>
          <SyncBadge status={status} />
          <div className="grow" />
          {/* one unit, so a long workspace name pushes the whole navigation to the next line */}
          <nav className="flex items-center gap-1">
          {handle && <HomeRelationsMenu handle={handle} workspaceId={workspaceId} readOnly={!canWrite} visibleRows={visibleRows} />}
          <Link to="/w/$workspaceId/graph" params={{ workspaceId }} className="rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100">{t("ws.nav.graph")}</Link>
          <Link to="/w/$workspaceId/review" params={{ workspaceId }} className="rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100">{t("ws.nav.review")}</Link>
          <Link to="/w/$workspaceId/settings" params={{ workspaceId }} className="rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100">{t("ws.nav.settings")}</Link>
          </nav>
        </div>
        {canWrite && (
          <form className="mb-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); void createPage(); }}>
            <Input placeholder={t("ws.newPageTitle")} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-page-title" />
            <Button type="submit" disabled={!handle} data-testid="new-page-submit">{t("ws.createPage")}</Button>
            <Button type="button" variant="secondary" disabled={!handle} onClick={() => handle && createFolder(handle.doc, t("tree.newFolderTitle"))} data-testid="new-folder">
              <FolderPlus size={14} /> {t("tree.newFolder")}
            </Button>
          </form>
        )}
        {canWrite && ws.data && <ImportPageButton workspaceId={workspaceId} />}
        {handle ? (
          <HomeTree
            workspaceId={workspaceId}
            handle={handle}
            pages={pages.data ?? []}
            canWrite={canWrite}
            sequences={sequences}
            visibleRows={visibleRows}
            onVisibleRows={setVisibleRows}
            onLink={setLinkPage}
            onNewPage={(parent) => void createPage(parent, true)}
          />
        ) : (
          <ul className="kss-surface" data-testid="page-tree" />
        )}
        <TrashPanel workspaceId={workspaceId} canRestore={canWrite} />
        {linkPage && handle && me.data && (
          // a page's links and facts (spec §8.25): workspace links with the page as subject
          <div className="fixed inset-y-0 right-0 z-40 flex shadow-[var(--shadow-lg)]">
            <LinksPanel
              subject={pageSubject(handle, workspaceId, linkPage, pageNodeOf(handle.doc, linkPage))}
              wsHandle={handle}
              workspaceId={workspaceId}
              userId={me.data.id}
              readOnly={!canWrite}
              title={pages.data?.find((p) => p.id === linkPage)?.title || t("page.untitled")}
              onClose={() => setLinkPage(null)}
            />
          </div>
        )}
      </div>
      <aside className="w-64 shrink-0 space-y-4 text-sm text-neutral-500">
        <SequencesSidebar wsHandle={handle} workspaceId={workspaceId} readOnly={!canWrite} />
        <ConceptsPanel wsHandle={handle} workspaceId={workspaceId} readOnly={!canWrite} />
      </aside>
    </div>
  );
}

/** The page tree with the workspace rail beside it (spec §8.25). */
function HomeTree(props: {
  workspaceId: string;
  handle: DocHandle;
  pages: import("../api/types").PageInfo[];
  canWrite: boolean;
  sequences: Array<{ id: string; label: string }>;
  visibleRows: PageRow[];
  onVisibleRows(rows: PageRow[]): void;
  onLink(docId: string): void;
  onNewPage(parent?: TreeID): void;
}) {
  const { handle, workspaceId } = props;
  const listRef = useRef<HTMLUListElement>(null);
  const allRows = useLoroSelector(handle.doc, (d) => readPageRows(d), samePageRows) ?? NO_ROWS;
  const rail = useWorkspaceRail({ handle, workspaceId, allRows, visibleRows: props.visibleRows, listRef });
  return (
    <PageTree
      ref={listRef}
      workspaceId={workspaceId}
      handle={handle}
      pages={props.pages}
      canWrite={props.canWrite}
      sequences={props.sequences}
      onLink={props.onLink}
      onAddToSequence={(seqId, iri) => addMember(handle.doc, seqId, iri)}
      onNewPage={props.onNewPage}
      onVisibleRows={props.onVisibleRows}
      overlay={rail.overlay}
      gutters={rail.gutters}
    />
  );
}

/** The relations menu for the workspace rail: it reads the same relations the rail draws. */
function HomeRelationsMenu({ handle, workspaceId, readOnly, visibleRows }: { handle: DocHandle; workspaceId: string; readOnly: boolean; visibleRows: PageRow[] }) {
  const listRef = useRef<HTMLElement>(null);
  const allRows = useLoroSelector(handle.doc, (d) => readPageRows(d), samePageRows) ?? NO_ROWS;
  const { scope } = useWorkspaceRail({ handle, workspaceId, allRows, visibleRows, listRef });
  return <RelationsMenu handle={handle} wsDoc={handle.doc} workspaceId={workspaceId} readOnly={readOnly} workspace={scope} />;
}

const NO_ROWS: PageRow[] = [];
